import { Component, inject, OnInit, ViewEncapsulation }             from '@angular/core';
import { HttpErrorResponse }                                        from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators }  from '@angular/forms';
import { MatError, MatFormField, MatFormFieldModule, MatLabel }     from '@angular/material/form-field';
import { MatInput }                                                 from '@angular/material/input';
import { MatProgressSpinner }                                       from '@angular/material/progress-spinner';
import { Router }                                                   from '@angular/router';

import { UserService }                 from '../../../api/user/user.service';
import { AuthenticationService }       from '../../../core/auth/authentication.service';
import { ButtonComponent, ButtonType } from '../../../shared/components/buttons/button.component';
import { PinNotificationComponent }    from '../../../shared/components/notification/notification.component';
import { InternalRoutes }              from '../../../shared/data/internal-routes';
import { ErrorService }                from '../../../core/error-handling/error.service';
import { FormUtils }                   from '../../../shared/utils/form.utils';
import { asciiOnlyValidator }          from '../../../shared/validators/ascii-only.validator';
import { StringUtils }                 from '../../../shared/utils/string.utils';

enum UsernameRemediationNotification {
  TryAgainLater = 'TryAgainLater'
}

@Component({
  selector: 'pin-username-remediation',
  templateUrl: './username-remediation.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [ButtonComponent, PinNotificationComponent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, MatFormFieldModule, MatProgressSpinner]
})
export class UsernameRemediationComponent implements OnInit {
  notificationEnum = UsernameRemediationNotification;
  notification: UsernameRemediationNotification;
  isLoading = false;
  assignedUsername: string;
  public remediationForm = new FormGroup({
    username: new FormControl<string>('', [Validators.required, asciiOnlyValidator(StringUtils.USERNAME_ILLEGAL_CHARACTERS)])
  });

  private router = inject(Router);
  private userService = inject(UserService);
  private authService = inject(AuthenticationService);
  private errorService = inject(ErrorService);

  ngOnInit() {
    this.isLoading = true;
    this.userService.getSuggestedUsername().subscribe({
      next: suggestedUsername => {
        this.assignedUsername = suggestedUsername;
        this.remediationForm.controls.username.setValue(suggestedUsername);
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;
        this.handleError(error);
      }
    });
  }

  confirmUsername() {
    if (!this.remediationForm.valid) {
      FormUtils.markAllControlsTouchedAndValidate(this.remediationForm);
      return;
    }
    this.submit(this.remediationForm.getRawValue().username);
  }

  private submit(newUsername: string) {
    if (this.isLoading) {
      return;
    }
    this.clearNotification();
    this.isLoading = true;

    this.userService.validateRemediationUsername(newUsername).subscribe({
      next: () => this.remediate(newUsername),
      error: error => {
        this.isLoading = false;
        this.handleError(error);
      }
    });
  }

  private remediate(newUsername: string) {
    this.userService.remediateUsername(newUsername).subscribe({
      next: () => this.logoutAndReturnToLogin(),
      error: error => {
        this.isLoading = false;
        this.handleError(error);
      }
    });
  }

  private logoutAndReturnToLogin() {
    const navigate = () => this.router
      .navigate(['/login'], {queryParams: {usernameRemediationChange: true}})
      .then(() => this.isLoading = false);
    this.authService.logout().subscribe({next: navigate, error: navigate});
  }

  backToLogin() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    const navigate = () => this.router.navigate(['/login']).then(() => this.isLoading = false);
    this.authService.logout().subscribe({next: navigate, error: navigate});
  }

  private handleError(error: HttpErrorResponse) {
    if (this.errorService.isInfrastructureError(error)) {
      this.notification = UsernameRemediationNotification.TryAgainLater;
      window.scroll(0, 0);
      return;
    }

    const errorMessage = typeof error?.error === 'string' ? error.error : error?.error?.message;
    switch (errorMessage) {
      case 'ERR_USERNAME_ALREADY_EXISTS':
        this.remediationForm.controls.username.setErrors({taken: true});
        this.remediationForm.controls.username.markAsTouched();
        break;
      case 'ERR_INVALID_PARAMETER':
      case 'ERR_REQUEST_VALIDATION_EXCEPTION':
        this.remediationForm.controls.username.setErrors({nonAsciiPrintable: true});
        this.remediationForm.controls.username.markAsTouched();
        break;
      default:
        this.router.navigate([InternalRoutes.ERROR]).then();
    }
    window.scroll(0, 0);
  }

  private clearNotification() {
    this.notification = undefined;
  }

  protected readonly buttonType = ButtonType;
}








